import { useContext, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, User, Lock, Mail } from 'lucide-react';
import { UserContext } from '../context/UserContext';
import './Auth.css';

interface FieldErrors {
    [key: string]: string;
}

function passwordStrength(password: string): { label: string; percent: number; color: string } {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[0-9]/.test(password) && /[a-z]/i.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { label: 'Weak', percent: 25, color: 'var(--color-error)' };
    if (score === 2) return { label: 'Fair', percent: 55, color: 'var(--color-warning)' };
    if (score === 3) return { label: 'Strong', percent: 80, color: 'var(--color-success)' };
    return { label: 'Excellent', percent: 100, color: 'var(--color-success)' };
}

export default function Auth(): React.JSX.Element {
    const location = useLocation();
    const navigate = useNavigate();
    const { login, register, registrationOpen, registrationLoading } = useContext(UserContext);
    const mode: 'login' | 'register' = location.pathname === '/register' ? 'register' : 'login';

    const [usernameOrEmail, setUsernameOrEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [keepSignedIn, setKeepSignedIn] = useState(false);
    const [acceptRules, setAcceptRules] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const strength = mode === 'register' ? passwordStrength(password) : null;

    function validate(): FieldErrors {
        const errors: FieldErrors = {};

        if (mode === 'login') {
            if (!usernameOrEmail.trim()) errors.usernameOrEmail = 'Required';
            if (!password) errors.password = 'Required';
        } else {
            if (!displayName.trim()) errors.displayName = 'Required';
            if (!username.trim()) errors.username = 'Required';
            else if (username.trim().length < 3) errors.username = 'Min 3 characters';
            if (!password) errors.password = 'Required';
            else if (password.length < 8) errors.password = 'Min 8 characters';
            if (!confirmPassword) errors.confirmPassword = 'Required';
            else if (confirmPassword !== password) errors.confirmPassword = 'Does not match';
        }

        return errors;
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setFormError('');

        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setFormError('Check the highlighted fields.');
            return;
        }
        if (mode === 'register' && !acceptRules) {
            setFormError('Accept the server rules to continue.');
            return;
        }

        setFieldErrors({});
        setSubmitting(true);
        try {
            const result =
                mode === 'login'
                    ? await login(usernameOrEmail.trim(), password)
                    : await register({
                          username: username.trim(),
                          password,
                          firstName: displayName.trim(),
                          email: email.trim() || undefined,
                      });

            if (!result.ok) {
                setFormError(result.error ?? 'Something went wrong.');
                return;
            }
            navigate('/home');
        } finally {
            setSubmitting(false);
        }
    }

    // Nothing is rendered until the server has said whether registration is
    // open: showing the wrong form for a frame and then yanking it away is
    // worse than a blank moment on an already-fast local request.
    if (registrationLoading) {
        return <div className="authPage" />;
    }

    // Registration is a one-shot bootstrap. Once it is closed the register
    // route must not exist for the user; while it is open (no account yet)
    // there is nothing to sign in to, so login sends them straight to it.
    if (mode === 'register' && !registrationOpen) {
        return <Navigate to="/login" replace />;
    }
    if (mode === 'login' && registrationOpen) {
        return <Navigate to="/register" replace />;
    }

    return (
        <div className="authPage">
            <form className="authCard" onSubmit={handleSubmit} noValidate>
                <h1 className="authTitle">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
                <p className="authSubtitle">
                    {mode === 'login' ? 'Sign in to your Musiceer library.' : 'Set up your Musiceer account.'}
                </p>

                {mode === 'login' ? (
                    <>
                        <label className="authField">
                            <span className="authLabel">Username or email</span>
                            <span className={`authInput ${fieldErrors.usernameOrEmail ? 'invalid' : ''}`}>
                                <User size={16} strokeWidth={2.75} />
                                <input
                                    type="text"
                                    placeholder="ada or ada@home.lan"
                                    value={usernameOrEmail}
                                    onChange={(e) => setUsernameOrEmail(e.target.value)}
                                    autoComplete="username"
                                />
                            </span>
                            {fieldErrors.usernameOrEmail && (
                                <span className="authError">{fieldErrors.usernameOrEmail}</span>
                            )}
                        </label>

                        <label className="authField">
                            <span className="authLabel">Password</span>
                            <span className={`authInput ${fieldErrors.password ? 'invalid' : ''}`}>
                                <Lock size={16} strokeWidth={2.75} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="authIconButton"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={16} strokeWidth={2.75} /> : <Eye size={16} strokeWidth={2.75} />}
                                </button>
                            </span>
                            {fieldErrors.password && <span className="authError">{fieldErrors.password}</span>}
                        </label>

                        <div className="authRow">
                            <label className="authCheckbox">
                                <input
                                    type="checkbox"
                                    checked={keepSignedIn}
                                    onChange={(e) => setKeepSignedIn(e.target.checked)}
                                />
                                Keep me signed in
                            </label>
                            <span className="authLink authLinkDecorative">Forgot password?</span>
                        </div>
                    </>
                ) : (
                    <>
                        <label className="authField">
                            <span className="authLabel">Display name</span>
                            <span className={`authInput ${fieldErrors.displayName ? 'invalid' : ''}`}>
                                <User size={16} strokeWidth={2.75} />
                                <input
                                    type="text"
                                    placeholder="Ada"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    autoComplete="name"
                                />
                            </span>
                            {fieldErrors.displayName && <span className="authError">{fieldErrors.displayName}</span>}
                        </label>

                        <label className="authField">
                            <span className="authLabel">Username</span>
                            <span className={`authInput ${fieldErrors.username ? 'invalid' : ''}`}>
                                <User size={16} strokeWidth={2.75} />
                                <input
                                    type="text"
                                    placeholder="ada"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoComplete="username"
                                />
                            </span>
                            {fieldErrors.username && <span className="authError">{fieldErrors.username}</span>}
                        </label>

                        <label className="authField">
                            <span className="authLabel">Password</span>
                            <span className={`authInput ${fieldErrors.password ? 'invalid' : ''}`}>
                                <Lock size={16} strokeWidth={2.75} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="authIconButton"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={16} strokeWidth={2.75} /> : <Eye size={16} strokeWidth={2.75} />}
                                </button>
                            </span>
                            {fieldErrors.password && <span className="authError">{fieldErrors.password}</span>}
                            {password && strength && (
                                <span className="strengthMeter">
                                    <span className="strengthTrack">
                                        <span
                                            className="strengthFill"
                                            style={{ width: `${strength.percent}%`, backgroundColor: strength.color }}
                                        />
                                    </span>
                                    <span className="strengthLabel" style={{ color: strength.color }}>
                                        {strength.label}
                                    </span>
                                </span>
                            )}
                        </label>

                        <label className="authField">
                            <span className="authLabel">Confirm password</span>
                            <span className={`authInput ${fieldErrors.confirmPassword ? 'invalid' : ''}`}>
                                <Lock size={16} strokeWidth={2.75} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Confirm password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </span>
                            {fieldErrors.confirmPassword && (
                                <span className="authError">{fieldErrors.confirmPassword}</span>
                            )}
                        </label>

                        <label className="authField">
                            <span className="authLabel">Email (optional)</span>
                            <span className="authInput">
                                <Mail size={16} strokeWidth={2.75} />
                                <input
                                    type="email"
                                    placeholder="ada@home.lan"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                />
                            </span>
                        </label>

                        <label className="authCheckbox">
                            <input
                                type="checkbox"
                                checked={acceptRules}
                                onChange={(e) => setAcceptRules(e.target.checked)}
                            />
                            I accept the server rules
                        </label>
                    </>
                )}

                {formError && <div className="authFormError">{formError}</div>}

                <button type="submit" className="authSubmit" disabled={submitting}>
                    {submitting ? (
                        <>
                            <Loader2 size={16} strokeWidth={2.75} className="spinner" />
                            {mode === 'login' ? 'Connecting…' : 'Creating account…'}
                        </>
                    ) : mode === 'login' ? (
                        'Sign in'
                    ) : (
                        'Create account'
                    )}
                </button>

                {/* No login<->register toggle: the two modes are mutually
                    exclusive. While registration is open there is no account
                    to sign in to, and once it is closed there is no account
                    left to create. */}
                {mode === 'register' && (
                    <p className="authToggle">This is the first account on this server and becomes its administrator.</p>
                )}
            </form>
        </div>
    );
}
